import Decimal from "decimal.js";
import { CreditFacilityExpenseAdjustmentsUpdateDataRequest } from "gen/model/creditFacilityExpenseAdjustmentsUpdateDataRequest";
import { DayCountEnum } from "gen/model/dayCountEnum";
import { GenericTableCell } from "gen/model/genericTableCell";
import { GenericTableRow } from "gen/model/genericTableRow";
import { MoneyTableCellValue } from "gen/model/moneyTableCellValue";
import { TextTableCellValue } from "gen/model/textTableCellValue";
import { get } from "lodash-es";
import { FieldValues, UseFormReturn, useWatch } from "react-hook-form";
import { useMaybernTableUIContext } from "shared/components/table/maybern/MaybernTableUIContext";
import { tryGetAgGridApi } from "shared/components/table/maybern/MaybernTableUtils";
import {
  getAnnualizedRateFromDailyRate,
  getDailyRateFromAnnualizedRate,
} from "shared/utils/DateUtils";
import { decimalOrZero } from "shared/utils/DecimalUtils";
import { useDeepCompareEffect } from "use-deep-compare";

// FIXME: once we decouple DataTable from Form, we can remove this but for now we need to manually watch the form and refresh cells on input changes
export const RefreshCellsOnInputChange = <T extends FieldValues>({
  form,
  children,
}: React.PropsWithChildren<{
  form: UseFormReturn<T>;
}>) => {
  const values = useWatch({
    control: form.control,
  });
  const { agGrid } = useMaybernTableUIContext();
  const agGridApi = tryGetAgGridApi(agGrid);
  useDeepCompareEffect(() => {
    if (agGridApi) {
      agGridApi.refreshCells();
    }
  }, [values]);
  return children;
};

/* eslint-disable @typescript-eslint/no-unnecessary-condition */
export const disableDataTableField = ({
  columnData,
  colId,
}: {
  columnData: GenericTableRow;
  colId: string;
}): GenericTableCell => ({
  ...columnData.cells[colId],
  form_field: {
    name: "",
    ...columnData.cells[colId]?.form_field,
    is_editable: false,
  },
});

export const getUpdatedOutstandingAmountWhenRateChanges = (
  columnData: GenericTableRow,
  formValues: CreditFacilityExpenseAdjustmentsUpdateDataRequest,
  rateDayCount: DayCountEnum,
): Decimal => {
  const effectiveDate = new Date(
    (columnData?.cells["effective_date"]?.value as TextTableCellValue)?.text ||
      "",
  );
  const principalOutstanding = decimalOrZero(
    (
      columnData.cells["principal_outstanding"]?.value as
        | MoneyTableCellValue
        | undefined
    )?.amount,
  );
  const adjustedRate = decimalOrZero(
    get(
      formValues,
      columnData.cells["interest_rate_adjustment"]?.form_field?.name as string,
    ),
  );
  const interestExpense = principalOutstanding.mul(
    getDailyRateFromAnnualizedRate(effectiveDate, adjustedRate, rateDayCount) ??
      1,
  );
  return interestExpense;
};

export const getUpdatedInterestRateWhenOutstandingChanges = (
  columnData: GenericTableRow,
  formValues: CreditFacilityExpenseAdjustmentsUpdateDataRequest,
  rateDayCount: DayCountEnum,
): Decimal | undefined => {
  const effectiveDate = new Date(
    (columnData?.cells["effective_date"]?.value as TextTableCellValue)?.text ||
      "",
  );
  const principalOutstanding = decimalOrZero(
    (
      columnData.cells["principal_outstanding"]?.value as
        | MoneyTableCellValue
        | undefined
    )?.amount,
  );
  const adjustedOutstanding = decimalOrZero(
    get(
      formValues,
      columnData.cells["interest_expense_amount_adjustment"]?.form_field
        ?.name as string,
    ),
  );
  const updatedInterestRate = getAnnualizedRateFromDailyRate(
    effectiveDate,
    adjustedOutstanding.dividedBy(principalOutstanding),
    rateDayCount,
  );
  return updatedInterestRate?.mul(100);
};
